import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import moment from 'moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageCommonService } from '../local-storage/local-storage-common.service';
import { AppLockComponent } from './app-lock.component';
import { AppLockModel } from './app-lock.model';
import { isDefined } from '../../utilities/is-defined';
import { DateManipulationCheckInterface } from '../../interfaces/date-manipulation-check.interface';
import { combineLatest, from, Observable, of, throwError } from 'rxjs';
import { UriUtilitiesService } from '../uri-utilities/uri-utilities.service';
import { HttpClient } from '@angular/common/http';
import { map, mergeMap, switchMap } from 'rxjs/operators';
import { CONNECTION_ERROR_CODES } from '../../enums/connection-error-codes.enum';
import { ODataIsAliveResponse } from '../../interfaces/is-alive-set.interface';
import { ENDPOINTS } from '../../enums/endpoints.enum';

@Injectable({
    providedIn: 'root',
})
export class DateManipulationCheckService implements DateManipulationCheckInterface {
    public isInitialized = false;

    private readonly LOCALSTORAGE_INTERNAL_TIMESTAMP_ID = 'LAST_VALID_INTERNAL_TIMESTAMP';
    private readonly LOCALSTORAGE_EXTERNAL_TIMESTAMP_ID = 'LAST_VALID_EXTERNAL_TIMESTAMP';

    private modalInstance: BsModalRef | undefined;

    // used to track timestamps generated based on the local system time; only allowed to move forward to be valid
    private lastValidInternalTimestamp = moment.invalid();

    // used to track timestamps received from external server (GW); always needs to be the same or before any local timestamp
    private lastValidExternalTimestamp = moment.invalid();
    public constructor(
        @Inject(LogInstanceToken) protected readonly log: LogInterface,
        private readonly localStorageCommonService: LocalStorageCommonService,
        private readonly modalService: BsModalService,
        private readonly uriUtilitiesService: UriUtilitiesService,
        private readonly httpClient: HttpClient,
        protected readonly translate: TranslateService,
    ) {}

    public init(): Observable<unknown> {
        this.isInitialized = true;
        const formatResponse = '?$format=json';
        const uriForExternalTimestamp =
            this.uriUtilitiesService.getUriForContent() + ENDPOINTS.IsAlive + formatResponse;

        return this.httpClient.get<ODataIsAliveResponse>(uriForExternalTimestamp).pipe(
            map((response) => moment.utc(response.d.results[0].TimeStamp, 'YYYYMMDDHHmmss')),
            mergeMap((timeStamp) => this.setExternalTimestamp(timeStamp)),
            switchMap(() =>
                combineLatest([
                    from(this.localStorageCommonService.storageGetAsync(this.LOCALSTORAGE_INTERNAL_TIMESTAMP_ID)),
                    from(this.localStorageCommonService.storageGetAsync(this.LOCALSTORAGE_EXTERNAL_TIMESTAMP_ID)),
                ]),
            ),
            mergeMap(([storedLastInternalTimestamp, storedLastExternalTimestamp]) => {
                this.lastValidInternalTimestamp =
                    isDefined(storedLastInternalTimestamp) &&
                    (typeof storedLastInternalTimestamp === 'string' || typeof storedLastInternalTimestamp === 'object')
                        ? moment.utc(storedLastInternalTimestamp)
                        : moment.utc();

                this.lastValidExternalTimestamp =
                    isDefined(storedLastExternalTimestamp) &&
                    (typeof storedLastExternalTimestamp === 'string' || typeof storedLastExternalTimestamp === 'object')
                        ? moment.utc(storedLastExternalTimestamp)
                        : moment.invalid();

                return this.dateManipulationCheck().pipe(map((result) => result));
            }),
        );
    }

    /**
     * Check if the given timestamp is valid, i.e. conforming to these two criteria:
     * 1. Timestamp needs to be after last internal timestamp
     * 2. Timestamp needs to be after external timestamp
     *
     * If yes, the stored valid timestamp gets updated with the new value; if not we display a modal that blocks the
     * application and therefore block the user from working.
     *
     * blockApplicationWhenInvalid:  - If true, a faulty date manipulation check will block the application.
     * - If false, a faulty date manipulation check allows the application to continue.
     *
     * @param timestamp
     */
    public async checkTimestamp(timestamp: moment.Moment): Promise<boolean> {
        if (!this.isInitialized) {
            throw new Error(
                `DateManipulationCheckService: checkTimestamp: Trying to call DateManipulationCheckService before initialization!`,
            );
        }
        const valid = await this.checkAndUpdateLastValidTimestamp(timestamp);

        // hide any modal that we might have displayed earlier
        this.modalInstance?.hide();
        this.modalInstance = undefined;

        if (!valid) {
            this.log.debug(
                `DateManipulationCheckService: checkTimestamp: Mismatching timestamps between system and external dates, blocking application`,
            );
            this.showDateTimeMismatchPopup();
        }

        return valid;
    }

    /**
     * Set the external timestamp as received from trusted source, eg. gateway
     *
     * @param timestamp
     */
    public setExternalTimestamp(timestamp: moment.Moment): Observable<void> {
        if (!this.isInitialized) {
            throw new Error(
                `DateManipulationCheckService: setExternalTimestamp: Trying to call DateManipulationCheckService before initialization!`,
            );
        }
        this.lastValidExternalTimestamp = timestamp;
        return from(
            this.localStorageCommonService.storageSetAsync(
                this.LOCALSTORAGE_EXTERNAL_TIMESTAMP_ID,
                timestamp.toISOString(),
            ),
        );
    }

    /**
     * Update the last valid timestamp.
     *
     * If the new timestamp is after the previously used timestamp, it will be updated and the function returns true.
     * If for some reason the passed in time is before the current last valid timestamp, the new value will be ignored
     * and false will be returned.
     *
     * @param time
     */
    private async checkAndUpdateLastValidTimestamp(time: moment.Moment): Promise<boolean> {
        if (!this.isInitialized) {
            throw new Error(
                `DateManipulationCheckService: checkAndUpdateLastValidTimestamp: Trying to call DateManipulationCheckService before initialization!`,
            );
        }
        this.log.debug(
            `DateManipulationCheckService: checkAndUpdateLastValidTimestamp: Current system time: ${moment().toISOString()}, time to check: ${time.toISOString()}, last valid internal timestamp: ${
                this.lastValidInternalTimestamp
            }, last valid external timestamp: ${this.lastValidExternalTimestamp}`,
        );

        // Allow for a tolerance of 1 minute deviation.
        // One use-case would perhaps be, if timestamp from the server is queried, but the response is delayed, the check
        // should still succeed within reason.
        const tolerance = moment.duration(1, 'minutes').asMilliseconds();
        const isInternallyValid = time.valueOf() >= this.lastValidInternalTimestamp.valueOf() - tolerance;

        let isExternallyValid = false;
        if (this.lastValidExternalTimestamp.isValid()) {
            isExternallyValid = time.valueOf() >= this.lastValidExternalTimestamp.valueOf() - tolerance;
        }

        if (
            this.lastValidInternalTimestamp.isValid() &&
            isInternallyValid &&
            this.lastValidExternalTimestamp.isValid() &&
            isExternallyValid
        ) {
            this.lastValidInternalTimestamp = time;
            await this.localStorageCommonService.storageSetAsync(
                this.LOCALSTORAGE_INTERNAL_TIMESTAMP_ID,
                time.toISOString(),
            );
            return true;
        }

        return false;
    }

    /**
     * Shows a blocking modal window without any controls that would allow the user to close the window, therefore
     * effectively blocking the application.
     *
     * The only way to unblock the application is to fix date and time and resume the application, triggering a new
     * check and a closing of the modal window.
     */
    private showDateTimeMismatchPopup(): void {
        if (!this.isInitialized) {
            throw new Error(
                `DateManipulationCheckService: showDateTimeMismatchPopup: Trying to call DateManipulationCheckService before initialization!`,
            );
        }
        const titleText = <string>this.translate.instant('APP_DATE_TIME_ERROR_POPUP_TITLE');
        const bodyText = `${<string>this.translate.instant('APP_DATE_TIME_ERROR_POPUP_BODY')} <br><br><b>${<string>(
            this.translate.instant('APP_DATE_TIME_SETTINGS_PATH')
        )}</b>`;

        this.modalInstance = this.modalService.show(AppLockComponent, {
            backdrop: 'static',
            class: 'modal-md',
            initialState: {
                model: new AppLockModel(titleText, bodyText),
            },
            ariaLabelledBy: 'app-lock-modal-title',
        });
    }

    /**
     * Check if date and time is valid or set correctly
     * If Invalid, we need to throw error and stop further initialising the application
     * If Valid, Proceed further initialisation
     *
     * @private
     */
    private dateManipulationCheck(): Observable<unknown> {
        return from(this.checkTimestamp(moment.utc())).pipe(
            map((isValid) => {
                if (!isValid) {
                    const error = new Error(CONNECTION_ERROR_CODES.INVALID_DATE_TIME_ERROR);
                    this.log.error(
                        `BaseInitializationService: attachDateTimeManipulationCheck: LastSyncTime is invalid`,
                    );
                    return throwError(() => error);
                }

                return of(undefined);
            }),
        );
    }
}
